// "Edit onboarding plan" overlay matching navigator_people_v83.html (`Ba`).
import React, { useState } from "react";
import { View, Text, StyleSheet, Pressable, ScrollView, Platform } from "react-native";
import { useSelector } from "react-redux";
import FontFamily from "../../../../constants/fonts";
import { MyTeamMember } from "../../types";
import { EmployeeOnboardingPlan, OnboardingChecklistRow, OnboardingPhaseKey } from "../types";
import { saveEmployeeOnboardingPlan } from "../../peopleHubApi";
import { DocumentModal, DocumentBanner, DocumentInput, DocColors } from "./OnboardingDocument";

interface EditOnboardingPlanModalProps {
  visible: boolean;
  onClose: () => void;
  plan: EmployeeOnboardingPlan;
  member: MyTeamMember;
  onSaveSuccess: () => void;
}

const PHASES: OnboardingPhaseKey[] = [
  "Week 1",
  "Week 2",
  "Week 3",
  "Milestone 1",
  "Milestone 2",
  "Milestone 3",
];

const PRIORITY_OPTIONS: OnboardingChecklistRow["priority"][] = ["Mandatory", "Recommended"];

// --nav-bg-selected / --nav-blue-500 / --nav-red-500 / --nav-red-600 (not in DocColors)
const SWITCHER_BG = "#EFF5FC";
const BLUE_500 = "#006AE6";
const RED_500 = "#C12C01";

// Native <select> on web (as in the mockup); a tap-to-toggle control elsewhere.
const PrioritySelect: React.FC<{
  value: OnboardingChecklistRow["priority"];
  disabled?: boolean;
  label: string;
  onChange: (value: OnboardingChecklistRow["priority"]) => void;
}> = ({ value, disabled, label, onChange }) => {
  const options = PRIORITY_OPTIONS.includes(value) ? PRIORITY_OPTIONS : [...PRIORITY_OPTIONS, value];

  if (Platform.OS === "web") {
    return React.createElement(
      "select",
      {
        value,
        disabled,
        "aria-label": label,
        onChange: (e: any) => onChange(e.target.value),
        style: {
          boxSizing: "border-box",
          width: 130,
          height: 32,
          padding: "4px 8px 4px 12px",
          border: `1px solid ${DocColors.inputBorder}`,
          borderRadius: 6,
          backgroundColor: DocColors.surface,
          color: disabled ? DocColors.textSecondary : DocColors.textPrimary,
          fontSize: 14,
          lineHeight: "20px",
          fontFamily: "inherit",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        },
      },
      options.map((opt) => React.createElement("option", { key: opt, value: opt }, opt)),
    );
  }

  return (
    <Pressable
      style={[styles.selectNative, disabled && styles.disabled]}
      disabled={disabled}
      onPress={() => onChange(options[(options.indexOf(value) + 1) % options.length])}
    >
      <Text style={styles.selectNativeText}>{value}</Text>
    </Pressable>
  );
};

const EditOnboardingPlanModal: React.FC<EditOnboardingPlanModalProps> = ({
  visible,
  onClose,
  plan,
  member,
  onSaveSuccess,
}) => {
  const selectedLocation = useSelector((state: any) => state.appData?.selectedLocation);
  const [activeTab, setActiveTab] = useState<"sections" | "kpis">("sections");
  const [selectedPhase, setSelectedPhase] = useState<OnboardingPhaseKey>("Week 1");
  const [customTitle, setCustomTitle] = useState("");
  const [rows, setRows] = useState(() => plan.rows || []);
  const [isSaving, setIsSaving] = useState(false);
  // Priority as loaded: required (Mandatory) items stay locked even if edited in this session.
  const [lockedByPriority] = useState(
    () => new Set((plan.rows || []).filter((r) => r.priority === "Mandatory").map((r) => r.id)),
  );

  if (!visible) return null;

  const currentPhaseRows = rows.filter((r) => r.phase === selectedPhase);
  const isDirty = JSON.stringify(rows) !== JSON.stringify(plan.rows || []);

  const handleClose = () => {
    if (
      isDirty &&
      Platform.OS === "web" &&
      typeof window !== "undefined" &&
      !window.confirm("Discard unsaved onboarding plan changes?")
    ) {
      return;
    }
    onClose();
  };

  const handleAddCustom = () => {
    if (!customTitle.trim()) return;
    const phasePrefix = selectedPhase.toLowerCase().replace(/\s+/g, "");
    const activitySlug = customTitle.trim().toLowerCase().replace(/\s+/g, "-");
    const generatedKey = `${phasePrefix}-${activitySlug}`;
    const newItem = {
      id: generatedKey,
      itemKey: generatedKey,
      phase: selectedPhase,
      activity: customTitle.trim(),
      type: "Training" as const,
      priority: "Recommended" as const,
      due: rows[0]?.due || "2026-11-14",
      status: "Open" as const,
      isImported: false,
    };
    setRows((prev) => [...prev, newItem]);
    setCustomTitle("");
  };

  const handleUpdate = (id: string, patch: Partial<OnboardingChecklistRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleRemove = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveEmployeeOnboardingPlan(
        member.gpid,
        { items: rows },
        selectedLocation?.name ?? selectedLocation?.displayText,
      );
      onSaveSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to save onboarding plan", err);
      alert(err?.response?.data?.message || err?.message || "Failed to save plan");
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: { id: "sections" | "kpis"; label: string }[] = [
    { id: "sections", label: "Plan Sections" },
    { id: "kpis", label: "KPI Ramp Targets, 12 Weeks" },
  ];

  return (
    <DocumentModal
      visible={visible}
      title={`Edit onboarding plan: ${member.name}`}
      onClose={handleClose}
      width={1040}
      secondaryAction={{ text: "Cancel", onPress: handleClose }}
      primaryAction={{ text: "Save Changes", onPress: handleSave, disabled: isSaving }}
    >
      {/* .switcher */}
      <View style={styles.switcher}>
        {tabs.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <Pressable
              key={tab.id}
              style={[styles.switcherOption, active && styles.switcherOptionActive]}
              onPress={() => setActiveTab(tab.id)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
            >
              <Text style={[styles.switcherText, active && styles.switcherTextActive]}>{tab.label}</Text>
            </Pressable>
          );
        })}
      </View>

      {activeTab === "sections" ? (
        <>
          <DocumentBanner text="Required, milestone, and completed records are locked to protect compliance and history. Recommended custom activities remain editable." />

          {/* .people-pills */}
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.pillsScroll}>
            <View style={styles.pills}>
              {PHASES.map((ph) => {
                const active = ph === selectedPhase;
                return (
                  <Pressable
                    key={ph}
                    style={[styles.pill, active && styles.pillActive]}
                    onPress={() => setSelectedPhase(ph)}
                  >
                    <Text style={[styles.pillText, active && styles.pillTextActive]}>{ph}</Text>
                  </Pressable>
                );
              })}
            </View>
          </ScrollView>

          {/* .people-editor-items */}
          <View style={styles.editorItems}>
            {currentPhaseRows.map((item) => {
              const locked =
                item.status === "Done" || item.type === "Milestone" || lockedByPriority.has(item.id);
              return (
                <View key={item.id} style={styles.editorItem}>
                  <View style={styles.editorInput}>
                    <DocumentInput
                      accessibilityLabel={`Activity in ${selectedPhase}`}
                      value={item.activity}
                      editable={!locked}
                      onChangeText={(text) => handleUpdate(item.id, { activity: text })}
                    />
                  </View>
                  <PrioritySelect
                    label={`Priority for ${item.activity}`}
                    value={item.priority}
                    disabled={locked}
                    onChange={(priority) => handleUpdate(item.id, { priority })}
                  />
                  <Pressable
                    style={[styles.removeButton, locked && styles.disabled]}
                    disabled={locked}
                    onPress={() => handleRemove(item.id)}
                  >
                    <Text style={styles.removeButtonText}>Remove</Text>
                  </Pressable>
                </View>
              );
            })}
            {currentPhaseRows.length === 0 && (
              <View style={styles.empty}>
                <Text style={styles.emptyTitle}>No {selectedPhase} activities</Text>
                <Text style={styles.emptyBody}>Add the first activity for this phase.</Text>
              </View>
            )}
          </View>

          {/* Add item */}
          <View style={styles.addRow}>
            <View style={styles.addField}>
              <Text style={styles.fieldLabel}>Add to {selectedPhase}</Text>
              <DocumentInput
                placeholder="New recommended onboarding activity"
                value={customTitle}
                onChangeText={setCustomTitle}
              />
            </View>
            <Pressable
              style={[styles.addButton, !customTitle.trim() && styles.disabled]}
              onPress={handleAddCustom}
              disabled={!customTitle.trim()}
            >
              <Text style={styles.addButtonText}>Add Item</Text>
            </Pressable>
          </View>
        </>
      ) : (
        <>
          <DocumentBanner text="These targets are governed by the employee location’s People Hub Configuration and cannot be changed on an individual plan." />
          <View style={styles.kpiCard}>
            <Text style={styles.kpiTitle}>Weekly Pulse Check Target Glidepath</Text>
            <Text style={styles.kpiSubtitle}>
              Metric ramp targets resolve from People Hub Configuration for this location.
            </Text>
            <View style={styles.kpiSummaryBox}>
              <Text style={styles.kpiSummaryText}>12 weeks glidepath · Milestones at Weeks 4, 8, 12</Text>
            </View>
          </View>
        </>
      )}
    </DocumentModal>
  );
};

const styles = StyleSheet.create({
  disabled: {
    opacity: 0.45,
  },
  // .switcher / .switcher-option
  switcher: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
    height: 32,
    padding: 2,
    borderRadius: 8,
    backgroundColor: SWITCHER_BG,
  },
  switcherOption: {
    paddingVertical: 3,
    paddingHorizontal: 14,
    borderRadius: 6,
    justifyContent: "center",
  },
  switcherOptionActive: {
    backgroundColor: DocColors.surface,
    shadowColor: DocColors.modalShadow,
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  },
  switcherText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textSecondary,
  },
  switcherTextActive: {
    fontFamily: FontFamily.bold,
    color: DocColors.blue800,
  },
  // .people-pills / .people-pill
  pillsScroll: {
    flexGrow: 0,
  },
  pills: {
    flexDirection: "row",
    gap: 6,
    paddingTop: 1,
    paddingBottom: 9,
  },
  pill: {
    minHeight: 32,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: DocColors.inputBorder,
    backgroundColor: DocColors.surface,
    justifyContent: "center",
  },
  pillActive: {
    borderColor: BLUE_500,
    backgroundColor: DocColors.blue25,
  },
  pillText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  pillTextActive: {
    fontFamily: FontFamily.bold,
    color: DocColors.blue800,
  },
  // .people-editor-items / .people-editor-item
  editorItems: {
    gap: 7,
  },
  editorItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  editorInput: {
    flex: 1,
    minWidth: 0,
  },
  selectNative: {
    width: 130,
    height: 32,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: DocColors.inputBorder,
    borderRadius: 6,
    backgroundColor: DocColors.surface,
    justifyContent: "center",
  },
  selectNativeText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: DocColors.textPrimary,
  },
  // .btn.btn-danger.btn-sm
  removeButton: {
    height: 32,
    paddingHorizontal: 16,
    borderRadius: 24,
    backgroundColor: RED_500,
    alignItems: "center",
    justifyContent: "center",
  },
  removeButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textOnDark,
  },
  empty: {
    alignItems: "center",
    paddingVertical: 24,
    gap: 4,
  },
  emptyTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },
  emptyBody: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  // add row (.row.gap-8 > .field + .btn.btn-secondary)
  addRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 8,
  },
  addField: {
    flex: 1,
    gap: 8,
  },
  fieldLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },
  addButton: {
    height: 40,
    paddingHorizontal: 24,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: DocColors.blue800,
    backgroundColor: DocColors.surface,
    alignItems: "center",
    justifyContent: "center",
  },
  addButtonText: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    lineHeight: 22,
    color: DocColors.blue800,
  },
  kpiCard: {
    borderWidth: 1,
    borderColor: DocColors.blue100,
    borderRadius: 10,
    backgroundColor: DocColors.pageBg,
    padding: 16,
    gap: 6,
  },
  kpiTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    lineHeight: 22,
    color: DocColors.textPrimary,
  },
  kpiSubtitle: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  kpiSummaryBox: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: DocColors.surface,
  },
  kpiSummaryText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue800,
  },
});

export default EditOnboardingPlanModal;
